import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";
import { uploadMedia } from "../media/api/mediaApi";

export type InstagramV2SelectedMedia = {
  mediaId: string;
  publicUrl: string;
  thumbnailUrl: string | null;
  fileName: string;
  mimeType: string;
  size?: number | null;
};

export type InstagramV2PendingMedia = {
  id: string;
  originalFile: File;
  previewUrl: string;
};

function sanitizeFileBaseName(value: string): string {
  const sanitized = value
    .trim()
    .replace(/[\\/:*?"<>|#%{}[\]]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return sanitized || "instagram_post";
}

function formatFileTimestamp(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    "_",
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
  ].join("");
}

function renameMediaFile(
  file: File,
  managementTitle: string,
  index: number,
  total: number,
  timestamp: string,
): File {
  const baseName = sanitizeFileBaseName(managementTitle);
  const dotIndex = file.name.lastIndexOf(".");
  const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : "";
  const sequence = total > 1 ? `_${String(index + 1).padStart(2, "0")}` : "";
  return new File([file], `${baseName}_${timestamp}${sequence}${extension}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export async function uploadInstagramV2PendingMediaFiles(args: {
  pendingMedia: InstagramV2PendingMedia[];
  managementTitle: string;
  existing: InstagramV2SelectedMedia[];
  setPendingMedia: (next: InstagramV2PendingMedia[]) => void;
  setSelected: (next: InstagramV2SelectedMedia[]) => void;
  setUploading: (uploading: boolean) => void;
}): Promise<InstagramV2SelectedMedia[]> {
  const { pendingMedia, managementTitle, existing } = args;
  if (pendingMedia.length === 0) return existing;

  const title = managementTitle.trim();
  if (!title) {
    throw new Error("管理用タイトルを入力してください。");
  }

  const timestamp = formatFileTimestamp();
  const total = pendingMedia.length;
  const renamedFiles = pendingMedia.map((item, index) =>
    renameMediaFile(item.originalFile, managementTitle, index, total, timestamp),
  );

  args.setUploading(true);
  try {
    const uploaded: InstagramV2SelectedMedia[] = [];
    for (const file of renamedFiles) {
      const item = await uploadMedia({ file, title: file.name });
      if (!item.source_url) throw new Error("アップロード後の公開URLが取得できませんでした");
      uploaded.push({
        mediaId: String(item.id),
        publicUrl: item.source_url,
        thumbnailUrl: item.thumbnail_url ?? null,
        fileName: item.title ?? file.name,
        mimeType: item.mime_type ?? file.type,
        size: item.file_size,
      });
    }
    pendingMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    args.setPendingMedia([]);
    const merged = [...existing, ...uploaded];
    const first = merged.length > 0 ? [merged[0]] : [];
    args.setSelected(first);
    return first;
  } finally {
    args.setUploading(false);
  }
}

export function InstagramV2MediaPicker({
  managementTitle,
  selected,
  pendingMedia,
  onPendingChange,
  onChange,
  onUploadingChange,
}: {
  managementTitle: string;
  selected: InstagramV2SelectedMedia[];
  pendingMedia: InstagramV2PendingMedia[];
  onPendingChange: (next: InstagramV2PendingMedia[]) => void;
  onChange: (next: InstagramV2SelectedMedia[]) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const { pick } = useUiLanguage();
  const [uploading, setUploading] = useState(false);
  const pendingRef = useRef(pendingMedia);
  pendingRef.current = pendingMedia;

  useEffect(() => {
    onUploadingChange(uploading);
  }, [onUploadingChange, uploading]);

  useEffect(() => {
    return () => {
      pendingRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  const selectedPreview = useMemo(() => {
    if (pendingMedia[0]) {
      return {
        url: pendingMedia[0].previewUrl,
        fileName: pendingMedia[0].originalFile.name,
        badge: pick("アップロード予定", "Pending upload"),
      };
    }
    if (selected[0]) {
      return {
        url: selected[0].thumbnailUrl ?? selected[0].publicUrl,
        fileName: selected[0].fileName,
        badge: pick("ライブラリ選択", "Library item"),
      };
    }
    return null;
  }, [pendingMedia, pick, selected]);

  const handleFilesSelected = (files: FileList | File[] | null) => {
    if (!files) return;
    const nextFiles = Array.from(files);
    if (nextFiles.length === 0) return;
    const nextMedia = nextFiles.map((file) => ({
      id: typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      originalFile: file,
      previewUrl: URL.createObjectURL(file),
    }));
    onPendingChange(nextMedia);
    onChange([]);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesSelected(event.target.files);
    event.target.value = "";
  };

  const removePending = (id: string) => {
    const target = pendingRef.current.find((item) => item.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    onPendingChange(pendingRef.current.filter((item) => item.id !== id));
  };

  return (
    <div className="igv2-media-picker">
      <div className="igv2-media-picker-top">
        <label className="igv2-media-upload-zone">
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileInputChange}
            disabled={uploading}
            style={{ display: "none" }}
          />
          {uploading ? <Loader2 size={20} className="igv2-spin" /> : <Upload size={20} />}
          <strong>{pick("クリックしてファイルを選択", "Click to choose files")}</strong>
          <span>{pick("管理タイトルは後で設定できます。保存時に自動でファイル名を整えます。", "You can set the internal title later. Filenames will be normalized on save.")}</span>
        </label>

        <div className="igv2-media-selected-card">
          <div className="igv2-media-selected-head">
            <strong>{pick("選択中メディア", "Selected media")}</strong>
            {selectedPreview ? <span>{selectedPreview.badge}</span> : null}
          </div>
          {selectedPreview ? (
            <div className="igv2-media-selected-preview">
              <img src={selectedPreview.url} alt={selectedPreview.fileName} />
              <div>
                <strong>{selectedPreview.fileName}</strong>
                <p>{managementTitle.trim() ? pick(`保存名の基準: ${managementTitle}`, `Save name base: ${managementTitle}`) : pick("管理タイトル未入力でも選択可能です", "You can select media before setting an internal title.")}</p>
              </div>
            </div>
          ) : (
            <div className="igv2-media-selected-empty">{pick("まだメディアが選択されていません", "No media selected yet")}</div>
          )}
          {pendingMedia.length > 0 ? (
            <div className="igv2-media-pending-list">
              {pendingMedia.map((item) => (
                <div key={item.id} className="igv2-media-pending-item">
                  <img src={item.previewUrl} alt={item.originalFile.name} />
                  <div>
                    <strong>{item.originalFile.name}</strong>
                    <span>{Math.round(item.originalFile.size / 1024)} KB</span>
                  </div>
                  <button type="button" className="igv2-compose-icon-button" onClick={() => removePending(item.id)} aria-label={pick("削除", "Remove")}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
